import React from 'react';
import { CheckCircleIcon, AlertTriangleIcon, GraduationCapIcon, ClockIcon, CalendarIcon, UsersIcon, BuildingIcon, DollarSignIcon, FileTextIcon, CheckIcon, XIcon } from 'lucide-react';
interface Step5ReviewProps {
  formData: any;
  updateFormData: (data: any) => void;
  onSubmit: (status: 'Draft' | 'Pending Approval' | 'Active') => void;
}
const Step5Review: React.FC<Step5ReviewProps> = ({
  formData,
  updateFormData,
  onSubmit
}) => {
  // Check if all required fields are filled
  const validateForm = () => {
    const requiredFields = ['programName', 'programType', 'description', 'deliveryMode', 'duration', 'tuition', 'campusLocation'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    // Check if learning outcomes has at least one non-empty item
    const hasLearningOutcomes = formData.learningOutcomes?.some((outcome: string) => outcome.trim() !== '');
    if (!hasLearningOutcomes) {
      missingFields.push('learningOutcomes');
    }
    // Check if intake dates has at least one item
    const hasIntakeDates = formData.intakeDates?.length > 0;
    if (!hasIntakeDates) {
      missingFields.push('intakeDates');
    }
    // Check if qualifications has at least one item
    const hasQualifications = formData.qualifications?.length > 0;
    if (!hasQualifications) {
      missingFields.push('qualifications');
    }
    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  };
  const validation = validateForm();
  return <div data-id="element-737">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-738">
        Review & Publish
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-739">
        Review all program information before publishing. You can go back to
        previous steps to make changes if needed.
      </p>
      {/* Validation Warning */}
      {!validation.isValid && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6" data-id="element-740">
          <div className="flex items-start" data-id="element-741">
            <AlertTriangleIcon className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-742" />
            <div data-id="element-743">
              <h3 className="font-medium text-yellow-800 mb-1" data-id="element-744">
                Missing Required Information
              </h3>
              <p className="text-sm text-yellow-700 mb-3" data-id="element-745">
                Please complete the following required fields before publishing:
              </p>
              <ul className="list-disc pl-5 text-sm text-yellow-700 space-y-1" data-id="element-746">
                {validation.missingFields.map((field, index) => <li key={index} data-id="element-747">
                    {field === 'programName' && 'Program Name'}
                    {field === 'programType' && 'Program Type'}
                    {field === 'description' && 'Program Description'}
                    {field === 'deliveryMode' && 'Mode of Delivery'}
                    {field === 'duration' && 'Program Duration'}
                    {field === 'tuition' && 'Tuition & Fees'}
                    {field === 'campusLocation' && 'Campus Location'}
                    {field === 'learningOutcomes' && 'Learning Outcomes'}
                    {field === 'intakeDates' && 'Intake Dates'}
                    {field === 'qualifications' && 'Required Qualifications'}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>}
      {/* Program Summary */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6" data-id="element-748">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-749">
          <div className="flex items-center" data-id="element-750">
            <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center mr-3" data-id="element-751">
              <GraduationCapIcon className="w-5 h-5 text-primary-500" data-id="element-752" />
            </div>
            <div data-id="element-753">
              <h3 className="text-lg font-medium text-gray-800" data-id="element-754">
                {formData.programName || 'Untitled Program'}
              </h3>
              <p className="text-sm text-gray-500" data-id="element-755">
                {formData.programType || 'No program type selected'} •{' '}
                {formData.deliveryMode || 'No delivery mode selected'}
              </p>
            </div>
          </div>
        </div>
        <div className="p-6" data-id="element-756">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-757">
            <div data-id="element-758">
              <h4 className="text-sm font-medium text-gray-500 mb-3" data-id="element-759">
                Program Overview
              </h4>
              <div className="space-y-4" data-id="element-760">
                <div data-id="element-761">
                  <h5 className="text-xs text-gray-500 mb-1" data-id="element-762">Description</h5>
                  <p className="text-sm text-gray-800" data-id="element-763">
                    {formData.description || 'No description provided'}
                  </p>
                </div>
                <div data-id="element-764">
                  <h5 className="text-xs text-gray-500 mb-1" data-id="element-765">
                    Learning Outcomes
                  </h5>
                  {formData.learningOutcomes?.length > 0 ? <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1" data-id="element-766">
                      {formData.learningOutcomes.map((outcome: string, index: number) => outcome.trim() && <li key={index} data-id="element-767">{outcome}</li>)}
                    </ul> : <p className="text-sm text-gray-500 italic" data-id="element-768">
                      No learning outcomes provided
                    </p>}
                </div>
                <div data-id="element-769">
                  <h5 className="text-xs text-gray-500 mb-1" data-id="element-770">
                    Target Students
                  </h5>
                  {formData.targetStudents?.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-771">
                      {formData.targetStudents.map((tag: string, index: number) => <span key={index} className="inline-block bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs" data-id="element-772">
                            {tag}
                          </span>)}
                    </div> : <p className="text-sm text-gray-500 italic" data-id="element-773">
                      No target students specified
                    </p>}
                </div>
              </div>
            </div>
            <div data-id="element-774">
              <h4 className="text-sm font-medium text-gray-500 mb-3" data-id="element-775">
                Program Details
              </h4>
              <div className="space-y-4" data-id="element-776">
                <div className="grid grid-cols-2 gap-4" data-id="element-777">
                  <div className="flex items-start" data-id="element-778">
                    <ClockIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5" data-id="element-779" />
                    <div data-id="element-780">
                      <h5 className="text-xs text-gray-500" data-id="element-781">Duration</h5>
                      <p className="text-sm text-gray-800" data-id="element-782">
                        {formData.duration || 'Not specified'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-783">
                    <DollarSignIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5" data-id="element-784" />
                    <div data-id="element-785">
                      <h5 className="text-xs text-gray-500" data-id="element-786">Tuition</h5>
                      <p className="text-sm text-gray-800" data-id="element-787">
                        {formData.tuition ? `AED ${formData.tuition}` : 'Not specified'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-788">
                    <CalendarIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5" data-id="element-789" />
                    <div data-id="element-790">
                      <h5 className="text-xs text-gray-500" data-id="element-791">Intake Periods</h5>
                      {formData.intakeDates?.length > 0 ? <ul className="text-sm text-gray-800" data-id="element-792">
                          {formData.intakeDates.map((date: string, index: number) => <li key={index} data-id="element-793">{date}</li>)}
                        </ul> : <p className="text-sm text-gray-500 italic" data-id="element-794">
                          None specified
                        </p>}
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-795">
                    <BuildingIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5" data-id="element-796" />
                    <div data-id="element-797">
                      <h5 className="text-xs text-gray-500" data-id="element-798">Campus</h5>
                      <p className="text-sm text-gray-800" data-id="element-799">
                        {formData.campusLocation || 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>
                <div data-id="element-800">
                  <h5 className="text-xs text-gray-500 mb-1" data-id="element-801">
                    Required Qualifications
                  </h5>
                  {formData.qualifications?.length > 0 ? <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1" data-id="element-802">
                      {formData.qualifications.map((qualification: string, index: number) => <li key={index} data-id="element-803">{qualification}</li>)}
                    </ul> : <p className="text-sm text-gray-500 italic" data-id="element-804">
                      No qualifications specified
                    </p>}
                </div>
                <div data-id="element-805">
                  <h5 className="text-xs text-gray-500 mb-1" data-id="element-806">Accreditation</h5>
                  <p className="text-sm text-gray-800" data-id="element-807">
                    {formData.accreditation || 'No accreditation information provided'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-6" data-id="element-808">
            <h4 className="text-sm font-medium text-gray-500 mb-3" data-id="element-809">
              Enrollment Form
            </h4>
            <div className="flex items-center" data-id="element-810">
              <FileTextIcon className="w-5 h-5 text-gray-400 mr-2" data-id="element-811" />
              <div data-id="element-812">
                <p className="text-sm text-gray-800" data-id="element-813">
                  {formData.formFields?.length > 0 ? `${formData.formFields.length} form fields configured` : 'Default application form will be used'}
                </p>
                <p className="text-xs text-gray-500" data-id="element-814">
                  {formData.formFields?.filter((f: any) => f.required).length}{' '}
                  required fields
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Publish Options */}
      
      <div className="mt-6 flex items-center justify-between" data-id="element-815">
        <div className="flex items-center text-sm text-gray-500" data-id="element-816">
          {validation.isValid ? <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-817" /> : <XIcon className="w-5 h-5 text-red-500 mr-2" data-id="element-818" />}
          {validation.isValid ? 'All required information is complete' : `${validation.missingFields.length} required fields need attention`}
        </div>
      </div>
    </div>;
};
export default Step5Review;